import '@google/model-viewer';

import { Box, Button, ButtonGroup, CircularProgress, createTheme, Stack, ThemeProvider, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CutImage from '../../shared/image/3d/cut.png';
import GrainImage from '../../shared/image/3d/grain.png';
import SeamImage from '../../shared/image/3d/seam.png';
import TrimImage from '../../shared/image/3d/trim.png';
import CanadaBeefLogo from '../../shared/image/logo.jpg';
import Wysiwyg from "../shared/Wysiwyg";

const GLB_MIME_TYPE = 'model/gltf-binary';

const theme = createTheme({
  palette: {
    secondary: {
      main: grey[400],
    },
  },
});

const getDirectusAssetUrl = (fileField) => {
  const fileId = typeof fileField === 'string' ? fileField : fileField?.id;
  return fileId ? `${process.env.REACT_APP_API_URL}/assets/${fileId}` : null;
};

const loadGlbObjectUrl = async (assetUrl) => {
  const response = await fetch(assetUrl);

  if (!response.ok) {
    throw new Error(`Failed to load 3D asset (${response.status})`);
  }

  const modelBuffer = await response.arrayBuffer();
  const modelBlob = new Blob([modelBuffer], { type: GLB_MIME_TYPE });

  return URL.createObjectURL(modelBlob);
};

export const ThreeDViewer = ({ plainModelFile, annotatedModelFile }) => {
  const { t } = useTranslation();
  const modelViewerRef = useRef(null);

  const plainModelUrl = getDirectusAssetUrl(plainModelFile);
  const annotatedModelUrl = getDirectusAssetUrl(annotatedModelFile);
  const hasPlainAndAnnotatedViews = Boolean(plainModelUrl && annotatedModelUrl);

  const [isPlainViewSelected, setIsPlainViewSelected] = useState(Boolean(plainModelUrl));
  const [modelObjectUrl, setModelObjectUrl] = useState(null);
  const [modelLoadState, setModelLoadState] = useState('idle');

  const activeModelUrl = useMemo(() => {
    if (hasPlainAndAnnotatedViews) {
      return isPlainViewSelected ? plainModelUrl : annotatedModelUrl;
    }

    return plainModelUrl || annotatedModelUrl;
  }, [
    annotatedModelUrl,
    hasPlainAndAnnotatedViews,
    isPlainViewSelected,
    plainModelUrl,
  ]);

  useEffect(() => {
    if (!activeModelUrl) {
      setModelObjectUrl(null);
      setModelLoadState('idle');
      return undefined;
    }

    let objectUrl;
    let isCancelled = false;

    const fetchModel = async () => {
      setModelLoadState('loading');
      setModelObjectUrl(null);

      try {
        objectUrl = await loadGlbObjectUrl(activeModelUrl);

        if (!isCancelled) {
          setModelObjectUrl(objectUrl);
        }
      } catch {
        if (!isCancelled) {
          setModelLoadState('error');
        }
      }
    };

    fetchModel();

    return () => {
      isCancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [activeModelUrl]);

  useEffect(() => {
    const viewer = modelViewerRef.current;

    if (!viewer || !modelObjectUrl) {
      return undefined;
    }

    viewer.src = modelObjectUrl;
    viewer.alt = t('3D');
    viewer.cameraControls = true;
    viewer.crossOrigin = 'anonymous';
    viewer.environmentImage = 'neutral';
    viewer.exposure = 1;
    viewer.shadowIntensity = 1;
    viewer.interactionPrompt = 'none';

    const handleModelLoaded = () => setModelLoadState('ready');
    const handleModelError = () => setModelLoadState('error');

    viewer.addEventListener('load', handleModelLoaded);
    viewer.addEventListener('error', handleModelError);

    return () => {
      viewer.removeEventListener('load', handleModelLoaded);
      viewer.removeEventListener('error', handleModelError);
    };
  }, [modelObjectUrl, t]);

  const legendItems = [
    {
      image: CutImage,
      label: 'Cut',
    },
    {
      image: GrainImage,
      label: 'Grain',
    },
    {
      image: SeamImage,
      label: 'Seam',
    },
    {
      image: TrimImage,
      label: 'Trim',
    },
  ];

  if (!activeModelUrl) {
    return null;
  }

  const isAnnotatedViewSelected = hasPlainAndAnnotatedViews
    ? !isPlainViewSelected
    : Boolean(!plainModelUrl && annotatedModelUrl);

  const isModelLoading = modelLoadState === 'loading' || modelLoadState === 'idle';
  const modelLoadFailed = modelLoadState === 'error';

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Stack
        className='three-d-viewer-stack'
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Box>
          {
            hasPlainAndAnnotatedViews && (
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <ThemeProvider theme={theme}>
                  <ButtonGroup
                    aria-label="3D view toggle"
                    color={'secondary'}
                  >
                    <Button
                      variant={isPlainViewSelected ? 'contained' : 'outlined'}
                      onClick={() => setIsPlainViewSelected(true)}
                      sx={{
                        color: '#212121',
                        textTransform: 'none',
                        boxShadow: 'none',
                      }}
                    >
                      { t('threedPlainView') }
                    </Button>
                    <Button
                      variant={!isPlainViewSelected ? 'contained' : 'outlined'}
                      onClick={() => setIsPlainViewSelected(false)}
                      sx={{
                        color: '#212121',
                        textTransform: 'none',
                        boxShadow: 'none',
                      }}
                    >
                      {t('threedAnnotatedView')}
                    </Button>
                  </ButtonGroup>
                </ThemeProvider>
              </Stack>
            )
          }
        </Box>

        <Box
          sx={{
            width: '6rem',
          }}
        >
          <img
            className="w-100"
            src={CanadaBeefLogo}
            alt={'Canada Beef'}
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '450px',
          backgroundColor: '#f6f6f6',
        }}
      >
        {isModelLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} />
          </Stack>
        )}

        {modelLoadFailed && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              px: 2,
            }}
          >
            <Typography color="error" align="center">
              Unable to load 3D model.
            </Typography>
          </Stack>
        )}

        <model-viewer
          ref={modelViewerRef}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      <Typography
        component={'div'}
        sx={{
          mt: 0.5,
          color: '#6a6868',
        }}
      >
        {t('threedDisclaimer')}
      </Typography>

      {
        isAnnotatedViewSelected && (
          <Box
            sx={{
              mt: 3,
            }}
          >
            <Stack
              direction={'row'}
              spacing={0}
              justifyContent={'center'}
            >
              {
                legendItems.map(item => (
                  <Box
                    key={item.label}
                    sx={{
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Stack
                      direction={'column'}
                      alignItems={'center'}
                    >
                      <Box>
                        <img
                          className='quality-guide-image'
                          src={item.image}
                          alt={item.label}
                          style={{ width: "5rem" }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 'bold',
                        }}
                        component={'div'}
                      >
                        <Wysiwyg>
                          {item.label}
                        </Wysiwyg>
                      </Typography>
                    </Stack>
                  </Box>
                ))
              }
            </Stack>
          </Box>
        )
      }
    </Box>
  );
};

export default ThreeDViewer;
